import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { Skeleton } from '@synoptic/ui-kit/skeleton/skeleton.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import {
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { clsx } from 'clsx';
import { QRCodeCanvas } from 'qrcode.react';
import { FC, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigation } from 'react-router';
import {
  formFieldsContainer,
  formHeading,
  submitAuthFormButton,
} from '../_auth/page.css.ts';
import {
  buttonsBox,
  detailedListItem,
  importantText,
  mfaSecret,
  qrCodeContainer,
  stepsList,
} from './setup-MFA-form.css.ts';
import { cardBody } from '@/routes/_auth/page.css';

type FormInputs = {
  code: string;
};

const defaultRenderHeader = (title: string) => (
  <h1 className={formHeading}>{title}</h1>
);

export const SetupMFAForm: FC<{
  onSetupMFA: () => void;
  onSkip: () => void;
  renderHeader?: (title: string) => React.ReactNode;
  username: string;
}> = ({ onSetupMFA, onSkip, username, renderHeader = defaultRenderHeader }) => {
  const { handleSubmit, register } = useForm<FormInputs>();
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const [secretLoading, setSecretLoading] = useState(true);
  const [secret, setSecret] = useState<string>();
  const [qrCodeStr, setQrCodeStr] = useState<string>('');
  const { location: navigationLocation } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!username) {
      return;
    }

    let ignore = false;
    setUpTOTP()
      .then((data) => {
        if (!ignore) {
          setQrCodeStr(
            'otpauth://totp/Synoptic:' +
              username +
              '?secret=' +
              data.sharedSecret +
              '&issuer=' +
              'Synoptic',
          );
          setSecret(data.sharedSecret);
        }
      })
      .catch(() => {
        toast.error({
          title: 'Failed to load MFA link',
          subTitle: 'Please try again later in account settings.',
        });
      })
      .finally(() => {
        setSecretLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [username, toast]);

  const submit: SubmitHandler<FormInputs> = async (data) => {
    setIsLoading(true);

    const { code } = data;
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });

      onSetupMFA();
    } catch (e) {
      setIsLoading(false);
      toast.error({ title: String(e) });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(submit)}>
      {renderHeader('Set up Two-factor Authentication')}
      <p className={cardBody}>
        Add an additional layer of security by requiring a verification code in
        addition to your password whenever you sign in.
      </p>
      <br />
      <div className={cardBody}>
        <span className={importantText}>Setup steps:</span>
        <ol className={stepsList}>
          <li>
            Download Google Authenticator app or use another Authenticator app
            of your choice.{' '}
          </li>
          <li>Open the app and select &quot;Add account&quot;.</li>
          <li className={detailedListItem}>
            Scan tne QR code below:
            <div className={qrCodeContainer} data-testid={'qr-code-container'}>
              {!secretLoading ? (
                <QRCodeCanvas value={qrCodeStr} />
              ) : (
                <Skeleton width={128} height={128} />
              )}
            </div>
            <div className={cardBody}>
              Or enter this secret key into your Authenticator app:
              <br />
              {!secretLoading ? (
                <span
                  className={clsx(mfaSecret, importantText)}
                  data-testid={'secret-key'}
                >
                  {secret}
                </span>
              ) : (
                <Skeleton width={200} height={21} />
              )}
            </div>
          </li>
          <li>
            Enter the 6-digit verification code generated by the authenticator
            app in the field below:
          </li>
        </ol>
      </div>
      <div className={formFieldsContainer}>
        <Input
          {...register('code', {
            required: true,
          })}
          placeholder={'Enter the verification code'}
          autoComplete={'off'}
          label={'Verification code'}
        />
        <div className={buttonsBox}>
          <Button
            className={submitAuthFormButton}
            size={'large'}
            type="submit"
            loading={
              isLoading && navigationLocation?.state.mfaAction === 'submit'
            }
            disabled={secretLoading || isLoading}
          >
            Verify and continue
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              setIsLoading(true);
              onSkip();
            }}
            className={submitAuthFormButton}
            size={'large'}
            disabled={isLoading}
            loading={
              isLoading && navigationLocation?.state.mfaAction === 'skip'
            }
            variant="ghost"
          >
            Skip for now
          </Button>
        </div>
      </div>
    </form>
  );
};
